import React, { useEffect, useRef, useState } from "react";
import UnlimitInput from "../ui-kit/unlimit-input";
import UnlimitTextarea from "../ui-kit/unlimit-textarea";
import UnlimitToggle from "../ui-kit/unlimit-toggle-2";
import UnlimitSubmitButton from "../ui-kit/unlimit-submit-button";
import UnlimitLoading from "../ui-kit/unlimit-loading";
import notificationService from "../../services/toaster.service";
import outletService from "../../services/http/stores/outlet.service";
import { hydrateErrors } from "../../utils/error.utils";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import UnlimitTable from "../ui-kit/unlimit-table";
import httpService from "../../services/http/peoples/company.service";
import "./company-account-form.css";

const CompanyAccountForm = ({
  onHide,
  companyId,
  accountId,
  createSubAccount,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState,
    control,
    setError,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      is_active: true,
    },
  });
  const { errors } = formState;
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [shops, setShops] = useState([]);
  const [shopLoading, setShopLoading] = useState(false);
  const [selectedShops, setSelectedShops] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const modalRef = useRef();

  // Fetch company account details for edit mode
  // Fetch company account details for edit mode
  const fetchCompanyAccountDetails = async () => {
    if (!accountId) return;

    try {
      const response = await httpService.getCompanyAccount(accountId);

      if (
        response &&
        response.status === 200 &&
        response.data &&
        response.data.data
      ) {
        const accountData = response.data.data;

        // Set form values
        setValue("lm_account_number", accountData.lm_account_number);
        setValue("account_name", accountData.account_name);
        setValue("remarks", accountData.remarks || "");
        setValue("is_active", accountData.is_active == "1");

        // Initialize shops selection if available
        if (accountData.shops && accountData.shops.length > 0) {
          const shopSelection = {};
          accountData.shops.forEach((shop) => {
            shopSelection[shop.id] = true;
          });
          setSelectedShops(shopSelection);
        }
      } else {
        notificationService.errorMessage(t("company.subAccountNotFound"));
      }
    } catch (error) {
      notificationService.errorMessage(t("general.error.fetchFailed"));
    }
  };

  // Fetch outlets/shops from API
  const fetchOutlets = async (editMode = false) => {
    try {
      const response = await outletService.outletList();

      if (
        response &&
        response.status === 200 &&
        response.data &&
        response.data.data
      ) {
        const outlets = response.data.data.data || [];

        // Transform the data to match our required format
        const formattedOutlets = outlets.map((outlet) => ({
          id: outlet.id,
          name: outlet.name || outlet.business_name,
          is_active: outlet.is_active === "1" || outlet.is_active === 1,
        }));

        setShops(formattedOutlets);

        // Initialize selectedShops state if not in edit mode
        if (!editMode) {
          const initialSelectedState = {};
          formattedOutlets.forEach((shop) => {
            initialSelectedState[shop.id] = false;
          });
          setSelectedShops(initialSelectedState);
        }
      }
    } catch (error) {
      // Optionally handle error
    }
  };

  // Unified API call with single loading state
  const apiCalls = async () => {
    setIsPageLoading(true);
    console.log("is page loading", isPageLoading);
    try {
      if (companyId) {
        await fetchOutlets(!!accountId);
      }
      if (accountId) {
        setIsEditMode(true);
        await fetchCompanyAccountDetails();
      } else {
        setIsEditMode(false);
      }
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    apiCalls();
  }, [companyId, accountId]);

  const onSubmitForm = async (data) => {
    try {
      setIsLoading(true);

      // Get the selected shop IDs
      const selectedShopIds = Object.entries(selectedShops)
        .filter(([_, isSelected]) => isSelected)
        .map(([id, _]) => id);

      // Prepare form data
      const formData = {
        company_id: companyId,
        lm_account_number: data.lm_account_number,
        account_name: data.account_name,
        remarks: data.remarks || "",
        is_active: data.is_active ? "1" : "0",
        shops: selectedShopIds, // Add selected shops
      };

      // Add ID for update case
      if (accountId) {
        formData.id = accountId;
      }

      try {
        let response;
        if (accountId) {
          // Update existing account
          response = await httpService.updateCompanyAccount(
            formData,
            accountId
          );
          notificationService.successMessage(
            t("company.subAccountUpdateSuccess")
          );
        } else {
          // Create new account
          response = await httpService.createCompanyAccount(formData);
          notificationService.successMessage(
            t("company.subAccountCreateSuccess")
          );
        }

        reset();
        onHide(true);
        modalHide();
      } catch (error) {
        if (error.validation_error?.errors) {
          hydrateErrors(error.validation_error.errors, setError);
        }
        notificationService.errorMessage(
          accountId
            ? t("general.error.updateFailed")
            : t("general.error.createFailed")
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const modalHide = () => {
    const cancelButton = document.querySelector(".btn-cancel");
    if (cancelButton) {
      cancelButton.click();
    }
  };

  const toggleShopStatus = (shopId, newStatus) => {
    setSelectedShops((prev) => ({
      ...prev,
      [shopId]: newStatus,
    }));
  };

  useEffect(() => {
    const modalElement = modalRef.current;

    const handleShow = () => {
      // Reset form or initialize values when modal shows
      if (companyId) {
        fetchOutlets();
      }

      if (accountId) {
        fetchCompanyAccountDetails();
      }
    };

    const handleHide = () => {
      reset();
      setSelectedShops({});
      setIsEditMode(false);
      onHide();
    };

    modalElement.addEventListener("show.bs.modal", handleShow);
    modalElement.addEventListener("hide.bs.modal", handleHide);

    return () => {
      modalElement.removeEventListener("show.bs.modal", handleShow);
      modalElement.removeEventListener("hide.bs.modal", handleHide);
    };
  }, [onHide, reset, companyId, accountId]);

  // Table columns for associated shops
  const shopColumns = [
    {
      title: t("tableColumnName.srNo"),
      key: "sr_no",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: t("tableColumnName.shopName"),
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: t("tableColumnName.status"),
      key: "status",
      width: 100,
      render: (_, record) => (
        <div className="d-flex justify-content-start status-toggle">
          <UnlimitToggle
            isActive={selectedShops[record.id] || false}
            label=""
            id={`shop_status_${record.id}`}
            toggleActive={(checked) => toggleShopStatus(record.id, checked)}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="modal fade" id="add-sub-account" ref={modalRef}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content px-2 pt-3">
                <button
                  type="button"
                  className="close w-100 justify-content-end text-black"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
                <div className="modal-body custom-modal-body">
                  {isPageLoading ? (
                    <div className="d-flex justify-content-center py-5">
                      <UnlimitLoading width={50} height={50} />
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit(onSubmitForm)}>
                      <div className="row">
                        <div className="col-12 mb-3">
                          <h4>
                            {accountId
                              ? t("company.editSubAccount")
                              : t("company.addSubAccount")}
                          </h4>
                        </div>

                        <div className="col-lg-12">
                          <div className="mb-3">
                            <UnlimitInput
                              label={t("inputField.label.lmAccountNumber")}
                              id="lm_account_number"
                              type="text"
                              placeholder={t(
                                "inputField.placeholder.enterLMAccountNumber"
                              )}
                              register={register}
                              rules={{
                                required: {
                                  value: true,
                                  message: t("inputField.validation.required"),
                                },
                                pattern: {
                                  value: /^\d+$/,
                                  message: t(
                                    "inputField.validation.invalidNumber"
                                  ),
                                },
                              }}
                              error={errors.lm_account_number}
                              errorMessage={errors.lm_account_number?.message}
                            />
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="mb-3">
                            <UnlimitInput
                              label={t("inputField.label.lmAccountName")}
                              id="account_name"
                              type="text"
                              placeholder={t(
                                "inputField.placeholder.enterLMAccountName"
                              )}
                              register={register}
                              rules={{
                                required: {
                                  value: true,
                                  message: t("inputField.validation.required"),
                                },
                              }}
                              error={errors.account_name}
                              errorMessage={errors.account_name?.message}
                            />
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="mb-3">
                            <UnlimitTextarea
                              label={t("inputField.label.noteRemarks")}
                              id="remarks"
                              placeholder={t(
                                "inputField.placeholder.enterNoteRemarks"
                              )}
                              register={register}
                              error={errors.remarks}
                              errorMessage={errors.remarks?.message}
                            />
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="mb-4">
                            <label className="mb-2">
                              {t("inputField.label.status")}
                            </label>
                             <div className="d-flex justify-content-start">
                              <UnlimitToggle
                                id="is_active"
                                isActive={watch("is_active")}
                                label=""
                                toggleActive={(checked) =>
                                  setValue("is_active", checked)
                                }
                              />
                             </div>
                          </div>
                        </div>

                        <div className="col-lg-12 mb-3">
                          <label className="mb-2">
                            {t("inputField.label.associateShop")}
                          </label>
                          {shopLoading ? (
                            <div className="d-flex justify-content-center py-3">
                              <UnlimitLoading width={40} height={40} />
                            </div>
                          ) : shops.length > 0 ? (
                            <div className="table-responsive">
                              <UnlimitTable
                                className="shop-association-table"
                                columns={shopColumns}
                                dataSource={shops}
                                rowKey="id"
                                pagination={false}
                                scroll={{ x: false }}
                                size="small"
                              />
                            </div>
                          ) : (
                            <div className="alert alert-info">
                              {t("general.noData")}{" "}
                              {t("general.noShopsAvailable")}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="modal-footer-btn mt-4">
                        <button
                          type="button"
                          className="btn btn-cancel me-2"
                          data-bs-dismiss="modal"
                        >
                          {t("general.button.cancel")}
                        </button>
                        <UnlimitSubmitButton
                          isLoading={isLoading}
                          label={
                            accountId
                              ? t("general.button.update")
                              : t("general.button.submit")
                          }
                        />
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyAccountForm;
